import React, { useContext, useEffect, useState } from 'react';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { Link } from '@hz-design-system/web-ui';

import { TListingClient, TListingSellerInfo } from 'types/TListing';
import { TSellerProfileClient } from 'types/TSellerProfile';

import SellerReviews from './SellerReviews';
import getFormattedLocation from '../../../../utils/getFormattedLocation';
import { trackGAEvent } from '../../../../utils/gaTracking';
import SellerProfilePicture from '../../../../components/SellerProfilePicture/SellerProfilePicture';
import { GA_TRACK_EVENTS_ACTIONS } from '../../../../utils/constants';

import Classes from './SellerInfoSmall.scss';
import EnvironmentContext from '../../../../contexts/EnvironmentContext';

type TSellerInfoProps = {
  seller: TListingSellerInfo;
  isAutomotiveAd: TListingClient['isAutomotiveAd'];
  profile: TSellerProfileClient;
  isAdmarkt: boolean;
};

let SmbPopOver;

const SellerInfoSmall = ({ seller, isAutomotiveAd, profile, isAdmarkt }: TSellerInfoProps) => {
  const {
    links: { sellerAnchor },
  } = useContext(EnvironmentContext);
  const { t } = useI18nContext();
  const { name, activeSinceDiff, location } = seller;
  const formattedLocation = getFormattedLocation(location, t);
  const isTrader = seller.sellerType === 'TRADER';
  const [isSmbPopOverLoaded, setIsSmbPopOverLoaded] = useState(false);
  const isSmbVerified = !isAutomotiveAd && Boolean(profile.smbVerified);
  const isProfileLoaded = Object.keys(profile).length !== 0;
  const firstReviewSystemData = profile.reviews?.[0] ?? {};
  const { numberOfReviews } = firstReviewSystemData;
  const hasNoReviews = !numberOfReviews && isProfileLoaded;
  const showReviews = !hasNoReviews || (!isAdmarkt && !isAutomotiveAd);

  useEffect(() => {
    if (isSmbVerified && !isSmbPopOverLoaded) {
      const loadSmbPopOver = async () => {
        try {
          SmbPopOver = (
            await import(
              /* webpackChunkName: "SmbPopOver" */ '../../../SellerInfoExpandedApp/components/components/SmbPopOver'
            )
          ).default;
          setIsSmbPopOverLoaded(true);
        } catch {}
      };

      loadSmbPopOver();
    }
  }, [isSmbPopOverLoaded, isSmbVerified]);

  const trackSellerNameClick = () => {
    trackGAEvent(GA_TRACK_EVENTS_ACTIONS.SELLER_SMALL_SECTION);
  };

  return (
    <div className={Classes.root}>
      <div className={Classes.image}>
        <SellerProfilePicture
          isLogo={isTrader}
          name={name}
          imageSrc={profile.profilePictures?.logoPicture || profile.profilePictures?.profilePicture}
          isLargeLogo
        />
      </div>
      <div className={Classes.section}>
        <div className={`${Classes.row} ${Classes.name}`}>
          <Link href={`#${sellerAnchor}`} onClick={trackSellerNameClick}>
            {name}
          </Link>
          {isSmbPopOverLoaded && <SmbPopOver />}
        </div>
        {isAutomotiveAd && (
          <>
            <div className={Classes.row}>{formattedLocation}</div>
            {showReviews && (
              <div className={Classes.row}>
                <SellerReviews seller={seller} profile={profile} />
              </div>
            )}
            {!showReviews && (
              <Trans
                className={`${Classes.row} TextEllipsis`}
                i18nKey="sellerInfo.activeOnPlatform"
                activeSinceDiff={activeSinceDiff}
              />
            )}
          </>
        )}
        {!isAutomotiveAd && (
          <>
            <Trans
              className={`${Classes.row} TextEllipsis`}
              i18nKey="sellerInfo.activeOnPlatform"
              activeSinceDiff={activeSinceDiff}
            />
            {showReviews && (
              <div className={Classes.row}>
                <SellerReviews seller={seller} profile={profile} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SellerInfoSmall;
